import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { presetRange, ymd } from "../../hooks/reports";
import { api } from "../../lib/api";
import { money } from "../../lib/format";
import { Empty, Pill, SectionHeader } from "../ui/Badges";

interface Entry { id: string; amount_fils: number; reason: string; status: "open" | "netted" | "settled" | "cancelled"; created_at: string; settled_at: string | null; batch_id: string | null; from_code: string; to_code: string; direction: "owes" | "owed" }
interface Position { status: string; batch_id: string | null; branch_a: string; branch_b: string; net_a_owes_b_fils: number; entries: number }
const TONE = { open: "amber", netted: "purple", settled: "green", cancelled: "gray" } as const;
const key = ["settlements"] as const;

/** Money one branch owes another (a sale or wallet top-up in one branch used in another): list, net per pair, close a period, mark paid. */
export function SettlementsPanel() {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const { toast, toastError } = useToast();
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const [range, setRange] = useState(presetRange("month"));
  const [busy, setBusy] = useState(false);
  const list = useQuery({ queryKey: [...key, "list", status], queryFn: () => api.get<{ settlements: Entry[]; totals: { owes_fils: number; owed_fils: number } }>(`/settlements${status ? `?status=${status}` : ""}`) });
  const net = useQuery({ queryKey: [...key, "net"], queryFn: async () => (await api.get<{ positions: Position[] }>("/settlements/net")).positions });
  const refresh = () => qc.invalidateQueries({ queryKey: key });

  async function close() {
    if (!window.confirm(t("settlements.confirmClose", { from: range.from, to: range.to }))) return;
    setBusy(true);
    try {
      const r = await api.post<{ batchId: string; entries: number }>("/settlements/close", { periodStart: range.from, periodEnd: range.to });
      toast(t("settlements.closed", { n: r.entries }), "success");
      await refresh();
    } catch (e) { toastError(e); } finally { setBusy(false); }
  }
  async function settle(batchId: string) {
    if (!window.confirm(t("settlements.confirmSettle"))) return;
    setBusy(true);
    try {
      await api.post(`/settlements/batches/${batchId}/settle`, {});
      toast(t("settlements.settled"), "success");
      await refresh();
    } catch (e) { toastError(e); } finally { setBusy(false); }
  }

  const positions = net.data ?? [];
  const entries = list.data?.settlements ?? [];
  const netted = positions.filter((p) => p.status === "netted" && p.batch_id);
  const batches = [...new Set(netted.map((p) => p.batch_id as string))];
  return (
    <section className="card space-y-4 p-4 sm:p-5" aria-label={t("settlements.title")}>
      <SectionHeader title={t("settlements.title")} subtitle={t("settlements.help")} />
      {list.data && (
        <div className="grid grid-cols-2 gap-2 text-xs font-bold">
          <div className="card p-2.5">{t("settlements.owes")}: <span className="num font-mono text-red-600">{money(list.data.totals.owes_fils)} {t("currency")}</span></div>
          <div className="card p-2.5">{t("settlements.owed")}: <span className="num font-mono text-emerald-700">{money(list.data.totals.owed_fils)} {t("currency")}</span></div>
        </div>
      )}
      {!!positions.length && (
        <div>
          <h3 className="mb-1.5 text-xs font-black text-gray-600">{t("settlements.net")}</h3>
          <div className="space-y-1.5">
            {positions.map((p, i) => (
              <div key={i} data-position={`${p.branch_a}-${p.branch_b}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-gray-50 px-3 py-2 text-xs font-bold">
                <span><span className="font-mono">{p.net_a_owes_b_fils >= 0 ? p.branch_a : p.branch_b}</span> → <span className="font-mono">{p.net_a_owes_b_fils >= 0 ? p.branch_b : p.branch_a}</span> · <span className="num font-mono">{money(Math.abs(p.net_a_owes_b_fils))} {t("currency")}</span></span>
                <span className="flex items-center gap-2"><Pill tone={TONE[p.status as keyof typeof TONE] ?? "gray"}>{t(`settlements.st.${p.status}`, { defaultValue: p.status })}</Pill><span className="text-gray-400">{t("settlements.entries", { n: p.entries })}</span></span>
              </div>
            ))}
          </div>
        </div>
      )}
      {isAdmin && (
        <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-gray-200 p-3">
          <div><label className="label" htmlFor="st-from">{t("reports.from")}</label><input id="st-from" className="field num !py-1.5" type="date" value={range.from} max={ymd(new Date())} onChange={(e) => setRange({ ...range, from: e.target.value })} /></div>
          <div><label className="label" htmlFor="st-to">{t("reports.to")}</label><input id="st-to" className="field num !py-1.5" type="date" value={range.to} max={ymd(new Date())} onChange={(e) => setRange({ ...range, to: e.target.value })} /></div>
          <button type="button" className="btn-primary" disabled={busy || range.from > range.to} onClick={() => void close()}>{t("settlements.close")}</button>
          {batches.map((b) => <button key={b} type="button" className="btn-ghost" disabled={busy} onClick={() => void settle(b)}>✓ {t("settlements.markPaid")}</button>)}
          <p className="basis-full text-[11px] font-semibold text-gray-500">{t("settlements.closeHelp")}</p>
        </div>
      )}
      <div className="flex items-center gap-2">
        <select className="field !w-auto !py-1.5" aria-label={t("settlements.status")} value={status} onChange={(e) => setStatus(e.target.value)}><option value="">{t("settlements.anyStatus")}</option>{(["open", "netted", "settled", "cancelled"] as const).map((s) => <option key={s} value={s}>{t(`settlements.st.${s}`)}</option>)}</select>
      </div>
      {list.isLoading ? <Empty>{t("common.loading")}</Empty> : !entries.length ? <Empty>{t("settlements.none")}</Empty> : (
        <div className="overflow-x-auto">
          <table className="w-full text-start text-xs">
            <thead className="border-b border-gray-100 bg-gray-50 text-[10px] font-black uppercase tracking-wider text-gray-500"><tr><th className="px-3 py-2 text-start">{t("settlements.when")}</th><th className="px-2 py-2 text-start">{t("settlements.fromTo")}</th><th className="px-2 py-2 text-start">{t("settlements.reason")}</th><th className="px-2 py-2 text-end">{t("settlements.amount")}</th><th className="px-2 py-2 text-center">{t("settlements.status")}</th></tr></thead>
            <tbody className="divide-y divide-gray-100 font-bold text-gray-700">
              {entries.map((e) => (
                <tr key={e.id}>
                  <td className="num px-3 py-2 font-mono">{String(e.created_at).slice(0, 10)}</td>
                  <td className="px-2 py-2 font-mono">{e.from_code} → {e.to_code}</td>
                  <td className="px-2 py-2 text-gray-500">{t(`settlements.reasons.${e.reason}`, { defaultValue: e.reason })}</td>
                  <td className="num px-2 py-2 text-end font-mono">{money(e.amount_fils)}</td>
                  <td className="px-2 py-2 text-center"><Pill tone={TONE[e.status]}>{t(`settlements.st.${e.status}`)}</Pill></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
